import Phaser from "phaser";
import type { ShadowConfig } from "../themes/types";

/**
 * Draw a rounded rectangle with border
 */
export function drawRoundedRect(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fillColor: number,
  borderColor: number,
  borderWidth: number = 3
): void {
  // Draw border (larger rounded rect)
  graphics.fillStyle(borderColor);
  graphics.fillRoundedRect(x, y, width, height, radius);

  // Draw fill (smaller rounded rect)
  graphics.fillStyle(fillColor);
  graphics.fillRoundedRect(
    x + borderWidth,
    y + borderWidth,
    width - borderWidth * 2,
    height - borderWidth * 2,
    radius - borderWidth
  );
}

/**
 * Draw a circle with border
 */
export function drawCircle(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
  fillColor: number,
  borderColor: number,
  borderWidth: number = 3
): void {
  // Draw border
  graphics.fillStyle(borderColor);
  graphics.fillCircle(x, y, radius);

  // Draw fill
  graphics.fillStyle(fillColor);
  graphics.fillCircle(x, y, radius - borderWidth);
}

/**
 * Draw a progress bar
 */
export function drawProgressBar(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  progress: number,
  emptyColor: number,
  fillColor: number,
  borderColor: number,
  borderWidth: number = 3
): void {
  const radius = height / 2;

  // Draw border
  graphics.fillStyle(borderColor);
  graphics.fillRoundedRect(x, y, width, height, radius);

  // Draw empty background
  graphics.fillStyle(emptyColor);
  graphics.fillRoundedRect(
    x + borderWidth,
    y + borderWidth,
    width - borderWidth * 2,
    height - borderWidth * 2,
    radius - borderWidth
  );

  // Draw fill
  if (progress > 0) {
    const fillWidth = (width - borderWidth * 2) * Math.min(1, Math.max(0, progress));
    graphics.fillStyle(fillColor);
    graphics.fillRoundedRect(
      x + borderWidth,
      y + borderWidth,
      fillWidth,
      height - borderWidth * 2,
      radius - borderWidth
    );
  }
}

/**
 * Draw a hexagon
 */
export function drawHexagon(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
  fillColor: number,
  borderColor: number,
  borderWidth: number = 3
): void {
  const points: number[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    points.push(x + radius * Math.cos(angle));
    points.push(y + radius * Math.sin(angle));
  }

  // Draw border
  graphics.fillStyle(borderColor);
  graphics.fillPoints(points, true);

  // Draw fill (smaller hexagon)
  const fillPoints: number[] = [];
  const fillRadius = radius - borderWidth;
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    fillPoints.push(x + fillRadius * Math.cos(angle));
    fillPoints.push(y + fillRadius * Math.sin(angle));
  }

  graphics.fillStyle(fillColor);
  graphics.fillPoints(fillPoints, true);
}

/**
 * Create a drop shadow effect
 */
export function createShadow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  config: ShadowConfig
): Phaser.GameObjects.Graphics {
  const shadow = scene.add.graphics();
  shadow.fillStyle(config.color, config.alpha);
  shadow.fillRoundedRect(
    x + config.x,
    y + config.y,
    width,
    height,
    height / 4
  );

  // Apply blur if supported
  if (config.blur > 0) {
    shadow.setPostPipeline("BlurPostFX");
  }

  return shadow;
}

/**
 * Draw a glossy overlay on a shape
 */
export function drawGlossyOverlay(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  topPercent: number = 0.4
): void {
  const overlayHeight = height * topPercent;

  // Top highlight
  graphics.fillStyle(0xffffff, 0.2);
  graphics.fillRoundedRect(x, y, width, overlayHeight, {
    tl: radius,
    tr: radius,
    bl: 0,
    br: 0,
  });

  // Bottom shadow
  graphics.fillStyle(0x000000, 0.15);
  graphics.fillRoundedRect(x, y + height - overlayHeight, width, overlayHeight, {
    tl: 0,
    tr: 0,
    bl: radius,
    br: radius,
  });
}

/**
 * Create a gradient fill (simulated with multiple rectangles)
 */
export function drawGradient(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  topColor: number,
  bottomColor: number,
  steps: number = 10
): void {
  const stepHeight = height / steps;

  for (let i = 0; i < steps; i++) {
    const color = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.ValueToColor(topColor),
      Phaser.Display.Color.ValueToColor(bottomColor),
      steps,
      i
    );

    graphics.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b));
    graphics.fillRect(x, y + i * stepHeight, width, stepHeight);
  }
}
